import { Input, Select } from "@clintonelec/react-storybook";
import { type Dispatch, Fragment, memo, type SetStateAction } from "react";
import type { NetworkIpSettings } from "Interfaces";
import { ipv4Regex } from "@clintonelec/typescriptutils";
import { NotificationType, notify } from "src/Notifications";
import {
	invalidIpValidityMessage, ipNetworkSettingsWarningMessage, NetworkType, networkTypeSelectOptions
} from "Data/Objects/Network";
import { produce } from "immer";

const ipv4SelectOptions = networkTypeSelectOptions.slice(1);

type Ipv4SetupSettingsProps = {
	ipSetupSettings: NetworkIpSettings;
	updateIpv4NetworkType: Dispatch<SetStateAction<NetworkType>>;
	updateIpv4Address: Dispatch<SetStateAction<string>>;
	updateSubnetMask: Dispatch<SetStateAction<string>>;
	updateIpv4Gateway: Dispatch<SetStateAction<string>>;
	updateDnsIpv4: (dnsIpv4: string[]) => void;
};

function Ipv4SetupSettings(props: Ipv4SetupSettingsProps) {
	const {
		ipSetupSettings: { ipv4Settings, ipv4Settings: { ipv4NetworkType } }, updateIpv4NetworkType,
		updateIpv4Address: handleIpv4AddressUpdate, updateSubnetMask: handleSubnetMaskupdate,
		updateIpv4Gateway: handleIpv4GatewayUpdate, updateDnsIpv4
	} = props;

	const disabled = ipv4NetworkType === NetworkType.DHCP_AUTOMATIC_DNS
		|| ipv4NetworkType === NetworkType.DHCP_MANUAL_DNS;

	const handleIpv4NetworkTypeSelect = (newIpv4NetworkType: string | null) => {
		if (!newIpv4NetworkType) {
			return;
		}

		if (newIpv4NetworkType === NetworkType.DHCP_AUTOMATIC_DNS) {
			notify(
				NotificationType.WARNING,
				"WARNING",
				ipNetworkSettingsWarningMessage,
				undefined,
				{ toastId: "ipv4SelectToast" }
			);
		}

		updateIpv4NetworkType(newIpv4NetworkType as NetworkType);
	};

	const handleUpdateDnsIpv4 = (index: number) => (newDnsIpv4: string) => {
		updateDnsIpv4(produce(ipv4Settings.dnsIpv4, draft => {
			draft[ index ] = newDnsIpv4;
		}));
	};

	const dnsFields = (
		ipv4Settings.dnsIpv4.map((dns, index) => {
			return (
				// eslint-disable-next-line react/no-array-index-key
				<div className="form-row" key={ `dns-ipv4-${ index }` }>
					<label>DNS Address { index + 1 }</label>
					<Input
						disabled={ ipv4NetworkType === NetworkType.DHCP_AUTOMATIC_DNS }
						name="dnsIpv4"
						onUpdate={ handleUpdateDnsIpv4(index) }
						value={ dns }
						validator={ ipv4Regex }
						validityMessage={ invalidIpValidityMessage }
						wrapClassName="form-input"
					/>
				</div>
			);
		})
	);

	return (
		<Fragment>
			<div className="form-row">
				<label>Network Type</label>
				<Select
					allowClear={ false }
					className="form-select"
					name="ipv4NetworkType"
					onSelect={ handleIpv4NetworkTypeSelect }
					options={ ipv4SelectOptions }
					value={ ipv4NetworkType }
				/>
			</div>
			<div className="form-row">
				<label>IP Address</label>
				<Input
					disabled={ disabled }
					name="ipv4Address"
					onUpdate={ handleIpv4AddressUpdate }
					value={ ipv4Settings.ipv4Address }
					validator={ ipv4Regex }
					validityMessage={ invalidIpValidityMessage }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<label>Subnet Mask</label>
				<Input
					disabled={ disabled }
					name="subnetMask"
					onUpdate={ handleSubnetMaskupdate }
					value={ ipv4Settings.subnetMask }
					validator={ ipv4Regex }
					validityMessage={ invalidIpValidityMessage }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<label>Default Gateway</label>
				<Input
					disabled={ disabled }
					name="ipv4Gateway"
					onUpdate={ handleIpv4GatewayUpdate }
					value={ ipv4Settings.ipv4Gateway }
					validator={ ipv4Regex }
					validityMessage={ invalidIpValidityMessage }
					wrapClassName="form-input"
				/>
			</div>
			{ dnsFields }
		</Fragment>
	);
}

export default memo(Ipv4SetupSettings);
